import { useState, useCallback, useMemo } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ReadabilityMetrics {
  wordCount: number;
  charCount: number;
  sentenceCount: number;
  paragraphCount: number;
  syllableCount: number;
  fleschKincaidGrade: number;
  fleschReadingEase: number;
  avgSentenceLength: number;
  avgWordLength: number;
  longestSentence: string;
  longestSentenceLength: number;
  readingTimeMinutes: number;
  speakingTimeMinutes: number;
  vocabularyRichness: number;
  sentenceComplexity: {
    simple: number;
    compound: number;
    complex: number;
  };
  topKeywords: { word: string; count: number; density: number }[];
}

// ─── Stop Words ───────────────────────────────────────────────────────────────
const STOP_WORDS = new Set([
  'the','a','an','and','or','but','in','on','at','to','for','of','with','by',
  'from','is','are','was','were','be','been','being','have','has','had','do',
  'does','did','will','would','could','should','may','might','shall','can',
  'not','no','nor','so','yet','both','either','neither','each','few','more',
  'most','other','some','such','than','that','these','they','this','those',
  'as','if','then','than','when','where','which','who','whom','whose','how',
  'what','why','all','any','both','each','every','its','it','he','she','we',
  'you','i','me','him','her','us','them','my','your','his','our','their',
  'into','through','during','before','after','above','below','between',
  'out','off','over','under','again','further','once','here','there',
  'about','against','along','among','around','because','while','although',
  'however','therefore','thus','hence','also','just','very','too','up',
  'down','only','own','same','so','than','too','very','s','t','can','will',
  'just','don','should','now','d','ll','m','o','re','ve','y','ain','aren',
  'couldn','didn','doesn','hadn','hasn','haven','isn','ma','mightn','mustn',
  'needn','shan','shouldn','wasn','weren','won','wouldn',
]);

// ─── Utility Functions ────────────────────────────────────────────────────────
function countSyllables(word: string): number {
  word = word.toLowerCase().replace(/[^a-z]/g, '');
  if (word.length <= 3) return 1;
  const vowels = 'aeiouy';
  let count = 0;
  let prevWasVowel = false;
  for (let i = 0; i < word.length; i++) {
    const isVowel = vowels.includes(word[i]);
    if (isVowel && !prevWasVowel) count++;
    prevWasVowel = isVowel;
  }
  if (word.endsWith('e')) count--;
  return Math.max(1, count);
}

function analyzeSentenceComplexity(sentence: string): 'simple' | 'compound' | 'complex' {
  const hasCoordinating = /\b(and|but|or|nor|for|yet|so)\b/i.test(sentence);
  const hasSubordinating = /\b(because|although|since|unless|while|if|when|where|after|before|until)\b/i.test(sentence);
  const commaCount = (sentence.match(/,/g) || []).length;
  if (hasSubordinating || commaCount >= 2) return 'complex';
  if (hasCoordinating) return 'compound';
  return 'simple';
}

function extractTopKeywords(text: string, wordCount: number): { word: string; count: number; density: number }[] {
  const words = text
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 3 && !STOP_WORDS.has(w));

  const freq: Record<string, number> = {};
  words.forEach((w) => { freq[w] = (freq[w] || 0) + 1; });

  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([word, count]) => ({
      word,
      count,
      density: wordCount > 0 ? parseFloat(((count / wordCount) * 100).toFixed(2)) : 0,
    }));
}

function analyzeText(text: string): ReadabilityMetrics {
  const charCount = text.length;
  const words = text.trim().split(/\s+/).filter((w) => w.length > 0);
  const wordCount = words.length;
  const paragraphs = text.split(/\n\n+/).filter((p) => p.trim().length > 0);
  const paragraphCount = Math.max(1, paragraphs.length);
  const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 0);
  const sentenceCount = Math.max(1, sentences.length);
  const syllableCount = words.reduce((sum, word) => sum + countSyllables(word), 0);
  const fleschKincaidGrade =
    wordCount > 0 && sentenceCount > 0
      ? 0.39 * (wordCount / sentenceCount) + 11.8 * (syllableCount / wordCount) - 15.59
      : 0;
  const fleschReadingEase =
    wordCount > 0 && sentenceCount > 0
      ? 206.835 - 1.015 * (wordCount / sentenceCount) - 84.6 * (syllableCount / wordCount)
      : 0;
  const avgSentenceLength = sentenceCount > 0 ? wordCount / sentenceCount : 0;
  const totalChars = words.reduce((sum, w) => sum + w.replace(/[^a-zA-Z]/g, '').length, 0);
  const avgWordLength = wordCount > 0 ? totalChars / wordCount : 0;
  let longestSentence = '';
  let longestSentenceLength = 0;
  sentences.forEach((s) => {
    const len = s.trim().split(/\s+/).length;
    if (len > longestSentenceLength) {
      longestSentenceLength = len;
      longestSentence = s.trim();
    }
  });
  const readingTimeMinutes = wordCount / 238;
  const speakingTimeMinutes = wordCount / 130;
  const uniqueWords = new Set(words.map((w) => w.toLowerCase().replace(/[^a-z]/g, '')));
  const vocabularyRichness = wordCount > 0 ? uniqueWords.size / wordCount : 0;
  const complexity = { simple: 0, compound: 0, complex: 0 };
  sentences.forEach((s) => { complexity[analyzeSentenceComplexity(s)]++; });
  const topKeywords = extractTopKeywords(text, wordCount);

  return {
    wordCount, charCount, sentenceCount, paragraphCount, syllableCount,
    fleschKincaidGrade: Math.max(0, fleschKincaidGrade),
    fleschReadingEase: Math.max(0, Math.min(100, fleschReadingEase)),
    avgSentenceLength, avgWordLength, longestSentence, longestSentenceLength,
    readingTimeMinutes, speakingTimeMinutes, vocabularyRichness,
    sentenceComplexity: complexity,
    topKeywords,
  };
}

function getGradeLabel(grade: number): { label: string; color: string; bg: string } {
  if (grade <= 6) return { label: 'Elementary School', color: 'text-emerald-600', bg: 'bg-emerald-50' };
  if (grade <= 8) return { label: 'Middle School', color: 'text-teal-600', bg: 'bg-teal-50' };
  if (grade <= 12) return { label: 'High School', color: 'text-sky-600', bg: 'bg-sky-50' };
  if (grade <= 16) return { label: 'College', color: 'text-indigo-600', bg: 'bg-indigo-50' };
  return { label: 'Graduate Level', color: 'text-purple-600', bg: 'bg-purple-50' };
}

function getReadingEaseLabel(score: number): { label: string; color: string; bg: string } {
  if (score >= 90) return { label: 'Very Easy', color: 'text-emerald-600', bg: 'bg-emerald-50' };
  if (score >= 80) return { label: 'Easy', color: 'text-teal-600', bg: 'bg-teal-50' };
  if (score >= 70) return { label: 'Fairly Easy', color: 'text-sky-600', bg: 'bg-sky-50' };
  if (score >= 60) return { label: 'Standard', color: 'text-sky-600', bg: 'bg-sky-50' };
  if (score >= 50) return { label: 'Fairly Difficult', color: 'text-amber-600', bg: 'bg-amber-50' };
  if (score >= 30) return { label: 'Difficult', color: 'text-orange-600', bg: 'bg-orange-50' };
  return { label: 'Very Confusing', color: 'text-red-600', bg: 'bg-red-50' };
}

function formatTime(minutes: number): string {
  if (minutes < 1) return `${Math.round(minutes * 60)} sec`;
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours}h ${mins}m`;
}

function getDensityColor(density: number): { bar: string; badge: string } {
  if (density >= 3) return { bar: 'bg-rose-500', badge: 'bg-rose-50 text-rose-700 border-rose-200' };
  if (density >= 2) return { bar: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200' };
  if (density >= 1) return { bar: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  return { bar: 'bg-sky-400', badge: 'bg-sky-50 text-sky-700 border-sky-200' };
}

const SAMPLE_TEXT = `Artificial intelligence has revolutionized the way we approach complex problems in modern society. Machine learning algorithms can now process vast amounts of data with unprecedented speed and accuracy. However, the ethical implications of AI deployment remain a subject of intense debate among researchers and policymakers.

The integration of AI systems into healthcare has shown promising results. Diagnostic tools powered by deep learning can identify patterns in medical imaging that human experts might overlook. Nevertheless, concerns about data privacy and algorithmic bias continue to challenge widespread adoption.

As we move forward, it is crucial to establish robust frameworks for AI governance. These frameworks must balance innovation with accountability, ensuring that technological advancement serves the broader interests of humanity while minimizing potential risks.`;

// ─── Keyword Density Chart ────────────────────────────────────────────────────
function KeywordDensityChart({ keywords }: { keywords: { word: string; count: number; density: number }[] }) {
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const maxCount = keywords[0]?.count ?? 1;

  return (
    <div className="rounded-2xl border-2 border-gray-100 bg-white p-5">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-extrabold text-gray-800 flex items-center gap-2">
          <i className="ri-bar-chart-horizontal-line text-emerald-500"></i>
          Keyword Density
        </h4>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-gray-100">
          <button
            onClick={() => setViewMode('chart')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              viewMode === 'chart' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-bar-chart-horizontal-line text-sm"></i>
            Chart
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              viewMode === 'table' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-table-line text-sm"></i>
            Table
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 p-3 rounded-xl bg-gray-50 border border-gray-100">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wide mr-1">Density:</span>
        {[
          { label: '≥ 3% Over-used', color: 'bg-rose-500' },
          { label: '2–3% High', color: 'bg-amber-500' },
          { label: '1–2% Optimal', color: 'bg-emerald-500' },
          { label: '< 1% Low', color: 'bg-sky-400' },
        ].map((item) => (
          <span key={item.label} className="flex items-center gap-1.5 text-xs text-gray-600 font-medium">
            <span className={`w-2.5 h-2.5 rounded-full ${item.color} flex-shrink-0`}></span>
            {item.label}
          </span>
        ))}
      </div>

      {viewMode === 'chart' ? (
        <div className="space-y-2.5">
          {keywords.map((kw, i) => {
            const colors = getDensityColor(kw.density);
            const barWidth = maxCount > 0 ? (kw.count / maxCount) * 100 : 0;
            return (
              <div key={kw.word} className="flex items-center gap-3">
                <span className="text-xs text-gray-400 font-bold w-5 text-right flex-shrink-0">{i + 1}</span>
                <span className="text-xs font-bold text-gray-700 w-24 truncate flex-shrink-0 capitalize">{kw.word}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden relative">
                  <div
                    className={`h-full ${colors.bar} rounded-full transition-all duration-700`}
                    style={{ width: `${barWidth}%` }}
                  ></div>
                  <span className="absolute inset-0 flex items-center pl-2 text-xs font-bold text-white mix-blend-difference">
                    {kw.count}×
                  </span>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${colors.badge}`}>
                  {kw.density}%
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-3 py-2.5 font-bold text-gray-500 uppercase tracking-wide">#</th>
                <th className="text-left px-3 py-2.5 font-bold text-gray-500 uppercase tracking-wide">Keyword</th>
                <th className="text-right px-3 py-2.5 font-bold text-gray-500 uppercase tracking-wide">Count</th>
                <th className="text-right px-3 py-2.5 font-bold text-gray-500 uppercase tracking-wide">Density</th>
                <th className="text-center px-3 py-2.5 font-bold text-gray-500 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody>
              {keywords.map((kw, i) => {
                const colors = getDensityColor(kw.density);
                const status =
                  kw.density >= 3 ? 'Over-used' :
                  kw.density >= 2 ? 'High' :
                  kw.density >= 1 ? 'Optimal' : 'Low';
                return (
                  <tr key={kw.word} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-3 py-2.5 text-gray-400 font-bold">{i + 1}</td>
                    <td className="px-3 py-2.5 font-bold text-gray-800 capitalize">{kw.word}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-gray-600">{kw.count}</td>
                    <td className="px-3 py-2.5 text-right font-bold text-gray-800">{kw.density}%</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full border text-xs font-bold ${colors.badge}`}>
                        {status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-3 leading-relaxed">
        <i className="ri-information-line mr-1"></i>
        Stop words (the, and, is…) are excluded. Optimal keyword density is <strong>1–2%</strong> for academic writing.
      </p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReadabilityPanel() {
  const [inputText, setInputText] = useState(SAMPLE_TEXT);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [metrics, setMetrics] = useState<ReadabilityMetrics | null>(null);

  const liveCounts = useMemo(() => {
    const words = inputText.trim().split(/\s+/).filter((w) => w.length > 0);
    const sentences = inputText.split(/[.!?]+/).filter((s) => s.trim().length > 0);
    const paragraphs = inputText.split(/\n\n+/).filter((p) => p.trim().length > 0);
    return {
      words: words.length,
      chars: inputText.length,
      sentences: sentences.length,
      paragraphs: Math.max(1, paragraphs.length),
    };
  }, [inputText]);

  const handleAnalyze = useCallback(() => {
    if (!inputText.trim()) return;
    const result = analyzeText(inputText);
    setMetrics(result);
    setHasAnalyzed(true);
  }, [inputText]);

  const handleReset = () => {
    setHasAnalyzed(false);
    setMetrics(null);
  };

  const gradeInfo = metrics ? getGradeLabel(metrics.fleschKincaidGrade) : null;
  const easeInfo = metrics ? getReadingEaseLabel(metrics.fleschReadingEase) : null;
  const totalComplexity = metrics
    ? metrics.sentenceComplexity.simple + metrics.sentenceComplexity.compound + metrics.sentenceComplexity.complex
    : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-100">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex-shrink-0">
          <i className="ri-bar-chart-box-line text-white text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-gray-900">Readability Analyzer</p>
          <p className="text-xs text-gray-500">Word Count · Reading Level · Keyword Density · Sentence Complexity</p>
        </div>
      </div>

      {/* Text Input */}
      <div className="rounded-2xl border-2 border-gray-200 overflow-hidden bg-white">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <span className="text-xs font-bold text-gray-600 flex items-center gap-2">
            <i className="ri-file-text-line text-sky-500"></i>
            Your Text
          </span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">{liveCounts.words} words</span>
            <span className="text-xs text-gray-300">·</span>
            <span className="text-xs text-gray-400">{liveCounts.chars} chars</span>
            <span className="text-xs text-gray-300">·</span>
            <span className="text-xs text-gray-400">{liveCounts.sentences} sentences</span>
            {hasAnalyzed && (
              <button
                onClick={handleReset}
                className="text-xs text-sky-600 font-semibold hover:text-sky-800 cursor-pointer whitespace-nowrap"
              >
                Edit text
              </button>
            )}
          </div>
        </div>
        <textarea
          value={inputText}
          onChange={(e) => { if (e.target.value.length <= 10000) setInputText(e.target.value); }}
          placeholder="Paste your text here to analyze readability…"
          rows={10}
          disabled={hasAnalyzed}
          className="w-full px-5 py-4 text-sm text-gray-800 placeholder-gray-400 focus:outline-none resize-none leading-relaxed disabled:bg-gray-50 disabled:text-gray-600"
        />
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
          <span className="text-xs text-gray-400">{inputText.length}/10,000 characters</span>
          <button
            onClick={handleAnalyze}
            disabled={!inputText.trim() || hasAnalyzed}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-sky-600 to-blue-600 text-white text-sm font-bold rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-bar-chart-box-line text-base"></i>
            {hasAnalyzed ? 'Analyzed' : 'Analyze Readability'}
          </button>
        </div>
      </div>

      {/* Results */}
      {hasAnalyzed && metrics && (
        <div className="space-y-6">
          {/* Reading Level Scores */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className={`rounded-2xl border-2 ${gradeInfo!.bg} ${gradeInfo!.color.replace('text-', 'border-').replace('-600', '-200')} p-5`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${gradeInfo!.bg.replace('-50', '-100')} flex-shrink-0`}>
                  <i className={`ri-graduation-cap-line ${gradeInfo!.color} text-lg`}></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Flesch-Kincaid Grade</p>
                  <p className={`text-2xl font-extrabold ${gradeInfo!.color} leading-none`}>{metrics.fleschKincaidGrade.toFixed(1)}</p>
                </div>
              </div>
              <div className={`px-3 py-2 rounded-xl ${gradeInfo!.bg.replace('-50', '-100')} text-center`}>
                <p className={`text-sm font-bold ${gradeInfo!.color}`}>{gradeInfo!.label}</p>
              </div>
            </div>

            <div className={`rounded-2xl border-2 ${easeInfo!.bg} ${easeInfo!.color.replace('text-', 'border-').replace('-600', '-200')} p-5`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${easeInfo!.bg.replace('-50', '-100')} flex-shrink-0`}>
                  <i className={`ri-dashboard-line ${easeInfo!.color} text-lg`}></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Reading Ease</p>
                  <p className={`text-2xl font-extrabold ${easeInfo!.color} leading-none`}>{metrics.fleschReadingEase.toFixed(0)}</p>
                </div>
              </div>
              <div className={`px-3 py-2 rounded-xl ${easeInfo!.bg.replace('-50', '-100')} text-center`}>
                <p className={`text-sm font-bold ${easeInfo!.color}`}>{easeInfo!.label}</p>
              </div>
            </div>
          </div>

          {/* Time Estimates */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border-2 border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex-shrink-0">
                <i className="ri-book-read-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Reading Time</p>
                <p className="text-xl font-extrabold text-gray-900">{formatTime(metrics.readingTimeMinutes)}</p>
                <p className="text-xs text-gray-400">@ 238 words/min</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border-2 border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 flex-shrink-0">
                <i className="ri-mic-line text-orange-600 text-xl"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Speaking Time</p>
                <p className="text-xl font-extrabold text-gray-900">{formatTime(metrics.speakingTimeMinutes)}</p>
                <p className="text-xs text-gray-400">@ 130 words/min</p>
              </div>
            </div>
          </div>

          {/* Text Statistics */}
          <div className="rounded-2xl border-2 border-gray-100 bg-white p-5">
            <h4 className="text-sm font-extrabold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-file-list-3-line text-sky-500"></i>
              Text Statistics
            </h4>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { label: 'Avg Sentence Length', value: `${metrics.avgSentenceLength.toFixed(1)} words`, icon: 'ri-text' },
                { label: 'Avg Word Length', value: `${metrics.avgWordLength.toFixed(1)} chars`, icon: 'ri-font-size' },
                { label: 'Vocabulary Richness', value: `${(metrics.vocabularyRichness * 100).toFixed(0)}%`, icon: 'ri-book-2-line' },
              ].map((stat, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-sky-100 flex-shrink-0">
                    <i className={`${stat.icon} text-sky-600 text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
                    <p className="text-sm font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Keyword Density Chart ── */}
          {metrics.topKeywords.length > 0 && (
            <KeywordDensityChart keywords={metrics.topKeywords} />
          )}

          {/* Sentence Complexity */}
          <div className="rounded-2xl border-2 border-gray-100 bg-white p-5">
            <h4 className="text-sm font-extrabold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-node-tree text-sky-500"></i>
              Sentence Complexity Breakdown
            </h4>
            <div className="space-y-3">
              {[
                { type: 'Simple', count: metrics.sentenceComplexity.simple, color: 'emerald', icon: 'ri-checkbox-circle-line' },
                { type: 'Compound', count: metrics.sentenceComplexity.compound, color: 'amber', icon: 'ri-link' },
                { type: 'Complex', count: metrics.sentenceComplexity.complex, color: 'rose', icon: 'ri-git-branch-line' },
              ].map((item) => {
                const pct = totalComplexity > 0 ? (item.count / totalComplexity) * 100 : 0;
                return (
                  <div key={item.type}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <i className={`${item.icon} text-${item.color}-500 text-sm`}></i>
                        {item.type}
                      </span>
                      <span className="text-sm font-bold text-gray-900">{item.count} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full bg-${item.color}-500 rounded-full transition-all duration-1000`}
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Longest Sentence */}
          {metrics.longestSentence && (
            <div className="rounded-2xl border-2 border-amber-100 bg-gradient-to-br from-amber-50 to-orange-50 p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
                  <i className="ri-ruler-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-800">Longest Sentence</p>
                  <p className="text-xs text-amber-600">{metrics.longestSentenceLength} words</p>
                </div>
              </div>
              <p className="text-sm text-amber-900 leading-relaxed italic">
                "{metrics.longestSentence.slice(0, 200)}{metrics.longestSentence.length > 200 ? '…' : ''}"
              </p>
            </div>
          )}

          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-sky-100 flex-shrink-0">
              <i className="ri-information-line text-sky-600 text-base"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-sky-800 mb-1">Understanding Your Scores</p>
              <p className="text-xs text-sky-700 leading-relaxed">
                <strong>Flesch-Kincaid Grade</strong> estimates the U.S. school grade level needed to understand your text.{' '}
                <strong>Reading Ease</strong> ranges 0–100; higher = simpler writing. Aim for 60–70 for general audiences.{' '}
                <strong>Keyword Density</strong> of 1–2% is optimal; above 3% may appear over-stuffed.
              </p>
            </div>
          </div>
        </div>
      )}

      {!hasAnalyzed && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
            <i className="ri-lightbulb-line text-amber-600 text-base"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-amber-800 mb-1">How the Readability Analyzer Works</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              Paste any text above and click <strong>Analyze Readability</strong>. The tool calculates Flesch-Kincaid Grade Level,
              Reading Ease score, estimated reading/speaking time, <strong>keyword density chart</strong>,
              sentence complexity breakdown, and vocabulary richness.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
